import React, { useEffect, useState } from "react";
import API from "../../services/api";
import { t } from "@lingui/macro";
import { i18n } from "@lingui/core";

const PromotionsTable = () => {
  const [promotions, setPromotions] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: "startDate", direction: "asc" });
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showFilter, setShowFilter] = useState(false);

  useEffect(() => {
    fetchPromotions();
  }, []);

  const fetchPromotions = async () => {
    try {
      const response = await API.get("/api/promotions/all");
      setPromotions(response.data);
    } catch (error) {
      console.error("Error fetching promotions:", error);
      setError(t`Failed to fetch promotions. Please try again.`);
    }
  };

  const getLocalizedDescription = (promotion) => {
    const lang = i18n.locale;
    if (lang.startsWith("es")) return promotion.descriptionEs;
    return promotion.descriptionEn;
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    const sortedPromotions = [...promotions].sort((a, b) => {
      let valA = a[key];
      let valB = b[key];

      if (key === "startDate" || key === "endDate") {
        valA = new Date(a[key]);
        valB = new Date(b[key]);
      }

      if (valA < valB) return direction === "asc" ? -1 : 1;
      if (valA > valB) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setSortConfig({ key, direction });
    setPromotions(sortedPromotions);
  };

  const handleDelete = async (id) => {
    try {
      const response = await API.delete(`/api/promotions/${id}`);
      if (response.status === 200) {
        setPromotions(promotions.filter((p) => p.id !== id));
      } else {
        setError(t`Failed to delete promotion.`);
      }
    } catch (error) {
      console.error("Error deleting promotion:", error);
      setError(t`Failed to delete promotion.`);
    }
  };

  const getStatus = (endDate) => {
    const currentDate = new Date();
    return new Date(endDate) >= currentDate ? "current" : "expired";
  };

  const filteredPromotions = promotions.filter((promotion) => {
    const status = getStatus(promotion.endDate);
    if (statusFilter === "all") return true;
    return status === statusFilter;
  });

  const toggleFilter = () => setShowFilter(!showFilter);

  return (
    <div className="dashboard-content">
      <h2>{t`Promotions List`}</h2>
      {error && <p className="error">{error}</p>}

      {filteredPromotions.length === 0 ? (
        <p>{t`No promotions found.`}</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th onClick={() => handleSort("type")}>
                {t`Type`} {sortConfig.key === "type" ? (sortConfig.direction === "asc" ? "🔼" : "🔽") : ""}
              </th>
              <th onClick={() => handleSort("discountValue")}>
                {t`Discount`} {sortConfig.key === "discountValue" ? (sortConfig.direction === "asc" ? "🔼" : "🔽") : ""}
              </th>
              <th onClick={() => handleSort("startDate")}>
                {t`Start Date`} {sortConfig.key === "startDate" ? (sortConfig.direction === "asc" ? "🔼" : "🔽") : ""}
              </th>
              <th onClick={() => handleSort("endDate")}>
                {t`End Date`} {sortConfig.key === "endDate" ? (sortConfig.direction === "asc" ? "🔼" : "🔽") : ""}
              </th>
              <th>{t`Price Categories`}</th>
              <th>{t`Description`}</th>
              <th onClick={toggleFilter}>
                {t`Status`}
                {showFilter && (
                  <div className="status-dropdown">
                    <button onClick={() => setStatusFilter("all")}>{t`All`}</button>
                    <button onClick={() => setStatusFilter("current")}>{t`Current`}</button>
                    <button onClick={() => setStatusFilter("expired")}>{t`Expired`}</button>
                  </div>
                )}
              </th>
              <th>{t`Actions`}</th>
            </tr>
          </thead>

          <tbody>
            {filteredPromotions.map((promotion, index) => {
              const status = getStatus(promotion.endDate);
              return (
                <tr key={promotion.id} className={index % 2 === 0 ? "even-row" : "odd-row"}>
                  <td>{promotion.type}</td>
                  <td>{promotion.discountValue != null ? promotion.discountValue : "-"}</td>
                  <td>{new Date(promotion.startDate).toLocaleDateString()}</td>
                  <td>{new Date(promotion.endDate).toLocaleDateString()}</td>
                  <td>{promotion.priceCategories.join(", ")}</td>
                  <td className="description-cell">
                    {getLocalizedDescription(promotion)?.slice(0, 60)}…
                  </td>
                  <td>
                    {status === "current" ? t`Current` : t`Expired`}
                  </td>
                  <td>
                    {status === "current" && (
                      <button className="delete-btn" onClick={() => handleDelete(promotion.id)}>
                        {t`Delete`}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default PromotionsTable;
